// EXPORT-CORE: Setup › General › Your data — "Export company" (offboarding-portability §3.3:
// export any time). Owners only. A modal asks for the owner's password (and a current two-step
// code when they use two-step sign-in), then downloads the Company Archive: a zip of Markdown,
// JSON, JSONL, CSV and the files as uploaded. The explanation sits behind the (i).
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  SettingsGroup,
  SettingsRow,
  toast,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { FileArchiveIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { saveBlob } from "../finance/finance-parts";
import { useSession } from "../use-feature";
import { problemText } from "./problem-text";

export function archiveName(slug: string, now = new Date()): string {
  return `companyos-archive-${slug}-${now.toISOString().slice(0, 10)}.zip`;
}

export function CompanyExportGroup({ api, company }: { api: Api; company: CompanyAccess }) {
  const [open, setOpen] = useState(false);
  return (
    <SettingsGroup
      id="your-data"
      data-slot="your-data"
      title={
        <span className="inline-flex items-center gap-1">
          Your data
          <InfoTip label="About the company export">
            One zip of everything the company owns: people, chart, DEs and AGs with their
            instructions and skills, memory, tasks, docs, chat, KPIs, files and billing. Open
            formats (Markdown, JSON, CSV). Private places and credentials stay out. Free, any time.
          </InfoTip>
        </span>
      }
    >
      <SettingsRow label="Export company" hint="Owners only. Asks for your password.">
        <Button
          variant="outline"
          size="sm"
          data-slot="export-company"
          onClick={() => setOpen(true)}
        >
          <FileArchiveIcon aria-hidden="true" />
          Export
        </Button>
      </SettingsRow>
      {open ? <ExportDialog api={api} company={company} onClose={() => setOpen(false)} /> : null}
    </SettingsGroup>
  );
}

function ExportDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const { me } = useSession();
  const twoStep = me.mfa.enrolled;
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const run = useMutation({
    mutationFn: () =>
      api.client.companies.export({
        params: { co: company.id },
        body: {
          ...(password ? { password } : {}),
          ...(twoStep && code ? { totp_code: code } : {}),
        },
      }),
    onSuccess: (blob) => {
      saveBlob(blob, archiveName(company.slug));
      toast.add({ title: "Company exported", description: archiveName(company.slug) });
      onClose();
    },
  });
  const ready = twoStep ? /^\d{6}$/.test(code) : password.length > 0;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready && !run.isPending) run.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-5"
          aria-label="Export company"
        >
          <DialogHeader>
            <DialogTitle>Export company</DialogTitle>
            <DialogDescription>Confirm it's you to download the archive.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="export-password">Password</FieldLabel>
            <Input
              id="export-password"
              type="password"
              autoComplete="current-password"
              className="h-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {twoStep ? (
            <Field>
              <FieldLabel htmlFor="export-code">Two-step code</FieldLabel>
              <Input
                id="export-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="h-10"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </Field>
          ) : null}
          {run.isError ? (
            <FieldError role="alert">
              {problemText(run.error, "The company was not exported.")}
            </FieldError>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={run.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready || run.isPending} data-slot="export-download">
              <FileArchiveIcon aria-hidden="true" />
              {run.isPending ? "Building…" : "Download archive"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
